import type { ISelectedAddon } from '../types';

export type NormalizedQuotePrice = {
    netPremium: number | null;
    grossPremium: number | null;
    gst: number | null;
};

export type NormalizedQuoteCover = {
    name: string | null;
    price: NormalizedQuotePrice;
};

export type NormalizedQuoteAddonLine = {
    addonId: string | null;
    name: string | null;
    price: number | null;
    gst: number | null;
};

export type NormalizedQuotePlan = {
    id: string | null;
    tenureUnit: string | null;
    covers: NormalizedQuoteCover[];
    addons: NormalizedQuoteAddonLine[];
    price: NormalizedQuotePrice;
};

function num(v: unknown): number | null {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

export function normalizeQuotePrice(raw: unknown): NormalizedQuotePrice {
    if (!raw || typeof raw !== 'object') {
        return { netPremium: null, grossPremium: null, gst: null };
    }
    const o = raw as Record<string, unknown>;
    const single = num(o.premium ?? o.amount);
    const net = num(o.net_premium ?? o.netPremium) ?? single;
    const gross = num(o.gross_premium ?? o.grossPremium) ?? single;
    return {
        netPremium: net,
        grossPremium: gross,
        gst: num(o.gst),
    };
}

function normalizeAddonPriceFromRaw(
    o: Record<string, unknown>
): NormalizedQuotePrice {
    const priceAsScalar =
        o.price != null && typeof o.price !== 'object' ? num(o.price) : null;

    const nested =
        o.price && typeof o.price === 'object'
            ? normalizeQuotePrice(o.price)
            : { netPremium: null, grossPremium: null, gst: null };

    const flatSingle =
        num(o.premium ?? o.amount ?? o.addon_premium ?? o.addonPremium) ??
        priceAsScalar;
    const flatGross = num(o.gross_premium ?? o.grossPremium);
    const flatNet = num(o.net_premium ?? o.netPremium);
    const flatGst = num(o.gst);

    let gross = nested.grossPremium ?? flatGross ?? flatSingle;
    let net = nested.netPremium ?? flatNet ?? flatSingle;
    const gst = nested.gst ?? flatGst;

    if (gross == null && net != null) {
        gross = net;
    }
    if (net == null && gross != null) {
        net = gross;
    }

    return {
        grossPremium: gross,
        netPremium: net,
        gst,
    };
}

function parseAddonScalarFields(o: Record<string, unknown>): {
    price: number | null;
    gst: number | null;
} {
    let price: number | null = null;
    if (o.price != null && typeof o.price !== 'object') {
        price = num(o.price);
    } else if (o.price && typeof o.price === 'object') {
        const p = normalizeQuotePrice(o.price);
        price = p.netPremium ?? p.grossPremium;
    } else {
        price =
            num(o.net_premium ?? o.netPremium) ??
            num(o.premium ?? o.amount) ??
            null;
    }
    return { price, gst: num(o.gst) };
}

function normalizeCover(raw: unknown): NormalizedQuoteCover | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    const name = o.name ?? o.cover_name;
    return {
        name: name != null ? String(name) : null,
        price: normalizeAddonPriceFromRaw(o),
    };
}

function normalizeAddonLine(raw: unknown): NormalizedQuoteAddonLine | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    const addonId = o.addon_id ?? o.addonId ?? o.id;
    if (addonId == null) return null;
    const name = o.name ?? o.addon_name;
    const { price, gst } = parseAddonScalarFields(o);
    return {
        addonId: String(addonId),
        name: name != null ? String(name) : null,
        price,
        gst,
    };
}

export function parseAddonsAvailable(raw: unknown): ISelectedAddon[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const o = item as Record<string, unknown>;
            const id = o.addon_id ?? o.addonId ?? o.id;
            if (id == null || id === '') return null;
            const { price, gst } = parseAddonScalarFields(o);
            const g = gst != null && Number.isFinite(gst) ? gst : 0;
            return {
                id: String(id),
                name: String(o.name ?? ''),
                price: price != null && Number.isFinite(price) ? price : 0,
                gst: g,
            };
        })
        .filter((x): x is ISelectedAddon => x != null);
}

export type ParsedQuotePriceResponse = {
    plan: NormalizedQuotePlan | null;
    variantId: string | null;
    addonsAvailable: ISelectedAddon[];
};

export function parseGetQuotePriceResponse(
    raw: unknown
): ParsedQuotePriceResponse {
    const d =
        raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const vid = d.variant_id ?? d.variantId;
    const variantId =
        vid !== undefined && vid !== null && vid !== '' ? String(vid) : null;

    const addonsAvailable = parseAddonsAvailable(
        d.addons_available ?? d.addonsAvailable
    );

    const planRaw = d.plan;
    if (planRaw && typeof planRaw === 'object') {
        const p = planRaw as Record<string, unknown>;
        const id = p.id ?? p.plan_id ?? p.planId;
        const tenureUnit = p.tenure_unit ?? p.tenureUnit;

        const coversCoerced = p.covers ?? p.cover;
        const covers: NormalizedQuoteCover[] = Array.isArray(coversCoerced)
            ? coversCoerced
                  .map((c) => normalizeCover(c))
                  .filter((c): c is NormalizedQuoteCover => c != null)
            : (() => {
                  const single = normalizeCover(coversCoerced);
                  return single ? [single] : [];
              })();

        const addonsRaw = p.addons;
        const addons: NormalizedQuoteAddonLine[] = Array.isArray(addonsRaw)
            ? addonsRaw
                  .map((a) => normalizeAddonLine(a))
                  .filter(
                      (line): line is NormalizedQuoteAddonLine => line != null
                  )
            : [];

        const priceObj = p.price ?? p.total_price ?? p.totalPrice;
        const price =
            priceObj && typeof priceObj === 'object'
                ? normalizeQuotePrice(priceObj)
                : normalizeAddonPriceFromRaw(p);

        return {
            plan: {
                id: id != null ? String(id) : null,
                tenureUnit: tenureUnit != null ? String(tenureUnit) : null,
                covers,
                addons,
                price,
            },
            variantId,
            addonsAvailable,
        };
    }

    const legacyQuote = d.quote_price ?? d.quotePrice;
    const legacyAmount = num(legacyQuote);
    if (legacyAmount != null) {
        return {
            plan: {
                id: null,
                tenureUnit: null,
                covers: [],
                addons: [],
                price: {
                    netPremium: legacyAmount,
                    grossPremium: null,
                    gst: null,
                },
            },
            variantId,
            addonsAvailable,
        };
    }

    return { plan: null, variantId, addonsAvailable };
}

export function roundTotalAmount(n: number | null | undefined): number | null {
    if (n === null || n === undefined || !Number.isFinite(Number(n))) {
        return null;
    }
    return Math.round(Number(n));
}

export function hasMeaningfulPlanPrice(
    price: NormalizedQuotePrice | undefined
): boolean {
    if (!price) return false;
    return (
        price.netPremium != null ||
        price.grossPremium != null ||
        price.gst != null
    );
}

export function getTotalPremiumInclGst(
    quotePlan: NormalizedQuotePlan | null | undefined
): number {
    const price = quotePlan?.price;
    if (price == null || !hasMeaningfulPlanPrice(price)) {
        return 0;
    }
    const { grossPremium, netPremium, gst } = price;
    if (grossPremium != null && Number.isFinite(Number(grossPremium))) {
        return Math.round(Number(grossPremium));
    }
    if (
        netPremium != null &&
        gst != null &&
        Number.isFinite(Number(netPremium)) &&
        Number.isFinite(Number(gst))
    ) {
        return Math.round(Number(netPremium) + Number(gst));
    }
    if (netPremium != null && Number.isFinite(Number(netPremium))) {
        return Math.round(Number(netPremium));
    }
    return 0;
}
